import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOrganizationDetails, getOrganizationMembers } from '../../services/apiService';
import Loading from '../shared/LoadingOverlay';
import { industryOptions, countryOptions, currencyOptions, languageOptions, countryTimezones } from '../../constants';

const OrganizationDetails = ({ id }) => {
    const { t } = useTranslation();
    const [details, setDetails] = useState(null);
    const [members, setMembers] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMembersLoading, setIsMembersLoading] = useState(true);
    const [error, setError] = useState(null);
    const [membersError, setMembersError] = useState(null);

    useEffect(() => {
        if (id) {
            getOrganizationDetails(id, setIsLoading, setDetails, setError);
            getOrganizationMembers(id, setIsMembersLoading, setMembers, setMembersError);
        }
    }, [id]);

    if (isLoading) return <div className="h-[300px] relative"><Loading /></div>;
    if (error) return <div className="p-6 text-red-500 text-center">{error}</div>;
    if (!details) return null;

    const getLabel = (options, value) => {
        const option = options.find(opt => opt.value === value);
        return option ? t(option.label) : value;
    };

    const getTimezoneLabel = (country, value) => {
        if (!country || !countryTimezones[country]) return value;
        const option = countryTimezones[country].find(opt => opt.value === value);
        return option ? t(option.label) : value;
    };

    const joinedDate = details.joinedAt && !Number.isNaN(Date.parse(details.joinedAt)) ? new Date(details.joinedAt) : null;

    const infoItems = [
        { label: t('org_name'), value: details.name },
        { label: t('org_slug'), value: details.slug },
        { label: t('org_domain'), value: details.domain },
        { label: t('org_website'), value: details.website, isLink: true },
        { label: t('org_industry'), value: getLabel(industryOptions, details.industry) },
        { label: t('org_size'), value: details.size },
        { label: t('org_country'), value: getLabel(countryOptions, details.country) },
        { label: t('org_timezone'), value: getTimezoneLabel(details.country, details.timezone) },
        { label: t('org_language'), value: getLabel(languageOptions, details.language) },
        { label: t('org_currency'), value: getLabel(currencyOptions, details.currency) },
    ];

    const membersList = Array.isArray(members)
        ? members
        : (members?.members || members?.data || []);

    const getMemberName = (member) => {
        const firstName = member.firstName ?? member.user?.firstName ?? '';
        const lastName = member.lastName ?? member.user?.lastName ?? '';
        return `${firstName} ${lastName}`.trim() || '-';
    };

    const getMemberEmail = (member) => member.email ?? member.user?.email ?? '-';

    return (
        <div className="p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center gap-6 mb-8">
                {details.logo && (
                    <div className="w-20 h-20 rounded-lg overflow-hidden border">
                        <img src={details.logo} alt={details.name} className="w-full h-full object-cover" />
                    </div>
                )}
                <div>
                    <h2 className="text-2xl font-bold text-taction">{details.name}</h2>
                    {joinedDate && <p className="text-gray-500">{t('joinedAt')}: {joinedDate.toLocaleDateString()}</p>}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {infoItems.map((item, index) => (
                    <div key={index} className="border-b pb-2">
                        <span className="block text-xs font-bold text-gray-400 uppercase mb-1">{item.label}</span>
                        {item.isLink ? (
                            <a href={item.value?.startsWith('http') ? item.value : `https://${item.value}`} target="_blank" rel="noopener noreferrer" className="text-taction hover:underline break-all">
                                {item.value || '-'}
                            </a>
                        ) : (
                            <span className="text-gray-700 break-all">{item.value || '-'}</span>
                        )}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t">
                <div className="text-center">
                    <span className="block text-2xl font-bold text-taction">{details.memberCount || 0}</span>
                    <span className="text-gray-500 text-xs uppercase">{t('members')}</span>
                </div>
                <div className="text-center">
                    <span className="block text-2xl font-bold text-taction">{details.campaignCount || 0}</span>
                    <span className="text-gray-500 text-xs uppercase">{t('campaigns')}</span>
                </div>
                <div className="text-center">
                    <span className="block text-2xl font-bold text-taction">{details.leadCount || 0}</span>
                    <span className="text-gray-500 text-xs uppercase">{t('leads')}</span>
                </div>
            </div>

            <div className="mt-8 pt-8 border-t">
                <h3 className="text-lg font-bold mb-4">{t('members_list')}</h3>
                {isMembersLoading ? (
                    <div className="h-20 relative"><Loading /></div>
                ) : membersError ? (
                    <p className="text-red-500 text-sm">{membersError}</p>
                ) : membersList.length > 0 ? (
                    <div className="flex flex-col gap-2">
                        {membersList.map((member, index) => (
                            <div key={member.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                <div>
                                    <p className="font-bold text-sm text-gray-800">{getMemberName(member)}</p>
                                    <p className="text-xs text-gray-500">{getMemberEmail(member)}</p>
                                </div>
                                <span className="text-xs bg-gray-200 px-2 py-1 rounded-full uppercase text-gray-600 font-bold">
                                    {member.role}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-gray-500 text-sm italic">No members found</p>
                )}
            </div>
        </div>
    );
};

export default OrganizationDetails;